import { useCallback, useRef, useSyncExternalStore } from 'react';
import deepEqual from 'fast-deep-equal';

let subscriberId = 0;

const subscriberMap = new Map<string, Record<number, (value: any) => void>>();

export const subscribableLocalStorage = {
  getItem<T>(key: string): T | null {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  },
  setItem<T>(key: string, value: T) {
    localStorage.setItem(key, JSON.stringify(value));
    const callbacks = subscriberMap.get(key) ?? {};
    for (const callback of Object.values(callbacks)) {
      callback(value);
    }
  },
  key(index: number) {
    return localStorage.key(index);
  },
  get length() {
    return localStorage.length;
  },
  removeItem(key: string) {
    localStorage.removeItem(key);
    const callbacks = subscriberMap.get(key) ?? {};
    for (const callback of Object.values(callbacks)) {
      callback(undefined);
    }
    subscriberMap.delete(key);
  },
  clear() {
    for (let i = 0; i < this.length; i++) {
      const key = this.key(i);
      if (!key) continue;
      this.removeItem(key);
    }
  },
  subscribe<T>(key: string, callback: (value: T) => void) {
    const id = subscriberId++;

    const callbacks = subscriberMap.get(key) ?? {};
    callbacks[id] = callback;
    subscriberMap.set(key, callbacks);

    return () => {
      const callbacks = subscriberMap.get(key) ?? {};
      delete callbacks[id];
      subscriberMap.set(key, callbacks);
    };
  },
};

function useShallow<U>(selector: () => U, dependencies: Array<unknown>) {
  const prev = useRef<U>();
  return useCallback(() => {
    if (
      typeof prev.current === 'undefined' ||
      !deepEqual(prev.current, selector())
    ) {
      prev.current = selector();
    }
    return prev.current!;
  }, dependencies);
}

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };

export type LocalStateAction<T extends JsonValue | undefined> = (
  value: T | undefined | ((prev: T | undefined) => T | undefined)
) => void;

export function useLocalStateAction<T extends JsonValue | undefined>(
  key: string,
  initialValue: T | undefined
): LocalStateAction<T> {
  return useCallback(
    (value) => {
      let newValue: T | undefined;
      if (typeof value === 'function') {
        const prevValue =
          subscribableLocalStorage.getItem<T>(key) ?? initialValue;
        newValue = value(prevValue);
      } else {
        newValue = value;
      }

      if (newValue === undefined) {
        subscribableLocalStorage.removeItem(key);
      } else {
        subscribableLocalStorage.setItem(key, newValue);
      }
    },
    [key]
  );
}

export function useLocalState<T extends JsonValue | undefined>(
  key: string,
  initialValue: T
): [T, LocalStateAction<T>] {
  const value = useSyncExternalStore(
    useCallback(
      (onStoreChange) => subscribableLocalStorage.subscribe(key, onStoreChange),
      [key]
    ),
    useShallow(() => subscribableLocalStorage.getItem<T>(key), [key])
  );

  const setter = useLocalStateAction<T>(key, initialValue);

  return [value ?? initialValue, setter];
}
